/**
 * SponsorDashboard — Dashboard for sponsors managing agent seats.
 *
 * Route: /sponsor/* (also /lender/* for backwards compat)
 * Tabs: Dashboard | Sponsored Agents | Compliance (lender/title/warranty only) | Billing
 *
 * Sponsor types: lender, title, brokerage, warranty, coach, association, vendor
 * RESPA compliance applies to lender type only.
 * KOPS-533: Renamed from LenderSponsorHub → SponsorDashboard
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faChartLine,
  faCreditCard,
  faShieldAlt,
  faPlus,
  faSpinner,
  faCircle,
  faCheckCircle,
  faTimesCircle,
  faDownload,
  faUserPlus,
  faExclamationTriangle,
  faClockRotateLeft,
  faColumns,
  faRobot,
  faPaperPlane,
  faBan,
  faEnvelope,
  faCalendarDay,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { HubShell, GoldButton, OutlineButton } from '../broker-intelligence/shared';
import type { HubTab, HubMetric } from '../broker-intelligence/shared';
import SplitPanel from '../broker-intelligence/shared/SplitPanel';
import AgentCoachingDetail from './AgentCoachingDetail';
import KanbanDealBoard from './KanbanDealBoard';
import SearchBar from '../broker-intelligence/shared/SearchBar';
import ListItem from '../broker-intelligence/shared/ListItem';
import { colors, fonts } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';
import Dropzone from 'components/base/Dropzone';
import imageIcon from 'assets/img/icons/cloud-upload.svg';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SponsoredAgent {
  id: string;
  userId?: string;
  name: string;
  email: string;
  phone?: string;
  status: string;
  joinedAt?: string;
  respaAcknowledged: boolean;
  respaAcknowledgedAt?: string;
  respaAgentTypedName?: string;
  invitationSent?: boolean;
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

// Sponsor type labels + config
// KOPS-642: Renamed "Lender Dashboard" → "Lender Portal" for lender sponsor type
const SPONSOR_TYPE_CONFIG: Record<string, { label: string; brandMiddle: string; showCompliance: boolean; complianceLabel: string }> = {
  lender:      { label: 'Lender Portal',                  brandMiddle: 'Lender',      showCompliance: true,  complianceLabel: 'RESPA Compliance' },
  title:       { label: 'Sponsor Partner Dashboard',      brandMiddle: 'Sponsor',     showCompliance: true,  complianceLabel: 'Compliance' },
  brokerage:   { label: 'Sponsor Partner Dashboard',      brandMiddle: 'Sponsor',     showCompliance: false, complianceLabel: '' },
  warranty:    { label: 'Sponsor Partner Dashboard',      brandMiddle: 'Sponsor',     showCompliance: true,  complianceLabel: 'Compliance' },
  coach:       { label: 'Sponsor Partner Dashboard',      brandMiddle: 'Sponsor',     showCompliance: false, complianceLabel: '' },
  association: { label: 'Sponsor Partner Dashboard',      brandMiddle: 'Sponsor',     showCompliance: false, complianceLabel: '' },
  vendor:      { label: 'Sponsor Partner Dashboard',      brandMiddle: 'Sponsor',     showCompliance: true,  complianceLabel: 'Compliance' },
};

function getTabsForType(sponsorType: string): HubTab[] {
  const cfg = SPONSOR_TYPE_CONFIG[sponsorType] || SPONSOR_TYPE_CONFIG.vendor;
  const tabs: HubTab[] = [
    { key: 'dashboard',   label: 'Dashboard',        icon: faChartLine },
    { key: 'agents',      label: 'Sponsored Agents', icon: faUsers },
    { key: 'coaching',    label: 'AI Coaching',       icon: faChartLine },
    { key: 'planner',     label: 'Weekly Planner',    icon: faUsers },
    { key: 'automations', label: 'Automations',        icon: faRobot },
  ];
  if (cfg.showCompliance) {
    tabs.push({ key: 'compliance', label: cfg.complianceLabel, icon: faShieldAlt });
  }
  // KOPS-642: Team management tab for multi-admin
  tabs.push({ key: 'team', label: 'Team', icon: faUsers });
  tabs.push({ key: 'settings', label: 'Settings', icon: faUsers });
  tabs.push({ key: 'billing', label: 'Billing', icon: faCreditCard });
  tabs.push({ key: 'help', label: 'Help & Support', icon: faShieldAlt });
  return tabs;
}

function getTabFromPath(pathname: string): string {
  const segments = pathname.replace(/\/$/, '').split('/');
  // Support both /sponsor/ and /lender/ routes
  const idx = Math.max(segments.indexOf('sponsor'), segments.indexOf('lender'));
  const after = idx >= 0 ? segments[idx + 1] : undefined;
  if (after && ['dashboard', 'agents', 'coaching', 'planner', 'compliance', 'billing', 'help', 'settings', 'team'].includes(after)) return after;
  return 'dashboard';
}

// ---------------------------------------------------------------------------
// Lender Email Connectivity (embedded, not full-page)
// ---------------------------------------------------------------------------

const LenderEmailConnectivity: React.FC<{ isLender: boolean }> = ({ isLender }) => {
  const [connectedEmails, setConnectedEmails] = useState<any[]>([]);
  const [emailLoading, setEmailLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Discovery state — same as agent portal
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [discoveredContacts, setDiscoveredContacts] = useState<any[]>([]);
  const [selectedDiscovered, setSelectedDiscovered] = useState<Set<string>>(new Set());
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryProgress, setDiscoveryProgress] = useState(0);
  const [importingLeads, setImportingLeads] = useState(false);

  // Check for OAuth redirect success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('microsoft') === 'connected' || params.get('email_connected') === 'true') {
      toast.success('Email account connected! Ready to scan for contacts.');
      params.delete('microsoft');
      params.delete('email_connected');
      const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
      // Auto-refresh connected emails and show discovery prompt
      axiosInstance.get('/connected-emails')
        .then(res => {
          setConnectedEmails(res.data.data || []);
          startDiscoveryPolling();
        })
        .catch(() => {});
    }
  }, []);

  const startDiscoveryPolling = () => {
    setShowDiscovery(true);
    setDiscoveryLoading(true);
    setDiscoveryProgress(0);
    setDiscoveredContacts([]);
    axiosInstance.get('/agents/discover-leads').then(() => {
      const pollInterval = setInterval(async () => {
        try {
          const res = await axiosInstance.get('/agents/discover-leads');
          const data = res.data;
          if (data.status === 'complete') {
            clearInterval(pollInterval);
            setDiscoveredContacts(data.contacts || []);
            setDiscoveryLoading(false);
            setDiscoveryProgress(100);
          } else if (data.status === 'error') {
            clearInterval(pollInterval);
            setDiscoveryLoading(false);
            toast.error(data.message || 'Discovery failed');
          } else if (data.status === 'scanning') {
            setDiscoveryProgress(data.progress || 0);
          }
        } catch {
          clearInterval(pollInterval);
          setDiscoveryLoading(false);
        }
      }, 3000);
    }).catch(() => { setDiscoveryLoading(false); });
  };

  const handleImportSelected = async () => {
    setImportingLeads(true);
    try {
      const toImport = discoveredContacts.filter((c: any) => selectedDiscovered.has(c.email));
      await axiosInstance.post('/agents/import-leads', { contacts: toImport });
      const pollImport = setInterval(async () => {
        try {
          const statusRes = await axiosInstance.get('/agents/import-leads-status');
          const data = statusRes.data;
          if (data.status === 'complete') {
            clearInterval(pollImport);
            toast.success(`Imported ${data.imported || toImport.length} contacts!`);
            setShowDiscovery(false);
            setImportingLeads(false);
          } else if (data.status === 'error') {
            clearInterval(pollImport);
            toast.error(data.message || 'Import failed');
            setImportingLeads(false);
          }
        } catch { clearInterval(pollImport); setImportingLeads(false); }
      }, 2000);
    } catch {
      toast.error('Failed to start import');
      setImportingLeads(false);
    }
  };

  useEffect(() => {
    axiosInstance.get('/connected-emails')
      .then(res => setConnectedEmails(res.data.data || []))
      .catch(err => console.error('Failed to load connected emails:', err))
      .finally(() => setEmailLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await axiosInstance.delete(`/connected-emails/${id}`);
      setConnectedEmails(prev => prev.filter(e => e.id !== id));
      toast.success('Email account disconnected');
    } catch {
      toast.error('Failed to disconnect email');
    } finally {
      setDeleting(null);
    }
  };

  const handleConnectGmail = () => {
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) { toast.error('Please log in again'); return; }
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8080' : '';
    window.location.href = `${baseUrl}/api/google/connect/auth?token=${encodeURIComponent(authToken)}`;
  };

  const handleConnectOutlook = () => {
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) { toast.error('Please log in again'); return; }
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8080' : '';
    window.location.href = `${baseUrl}/api/microsoft/connect/auth?token=${encodeURIComponent(authToken)}`;
  };

  const [showBrowserLogin, setShowBrowserLogin] = useState(false);

  const handleConnectOutlookBrowser = () => {
    setShowBrowserLogin(!showBrowserLogin);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginTop: 24, border: `1px solid ${colors.border}` }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 8px', fontFamily: fonts.body }}>
        <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: colors.gold }} />
        Email Integration
      </h3>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
        Connect your {isLender ? 'Outlook or Gmail' : 'Gmail or Outlook'} to send emails directly from your account and automatically sync conversations with your {isLender ? 'borrowers' : 'clients'}.
      </p>

      {emailLoading ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ color: colors.gold, fontSize: 20 }} />
        </div>
      ) : (
        <>
          {/* Connected accounts list */}
          {connectedEmails.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy, marginBottom: 12 }}>Connected Accounts</div>
              {connectedEmails.map((email: any) => (
                <div key={email.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', borderRadius: 8, marginBottom: 8,
                  background: '#f8f9fc', border: `1px solid ${colors.border}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: email.provider === 'microsoft' ? 'linear-gradient(135deg, #0078d4, #40a9ff)' : 'linear-gradient(135deg, #ea4335, #4285f4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <FontAwesomeIcon icon={faEnvelope} style={{ color: '#fff', fontSize: 14 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{email.email}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {email.provider === 'microsoft' ? 'Microsoft/Outlook' : 'Gmail'} · Connected
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(email.id)}
                    disabled={deleting === email.id}
                    style={{
                      background: 'none', border: 'none', color: '#dc3545',
                      cursor: 'pointer', fontSize: 12, padding: '4px 8px'
                    }}
                  >
                    {deleting === email.id ? 'Removing...' : 'Disconnect'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Connect buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={handleConnectOutlook} style={{
              flex: 1, padding: '12px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontSize: 13, fontWeight: 600, color: '#0078d4', transition: 'all 0.2s'
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #0078d4, #40a9ff)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#fff', fontSize: 11 }} />
              </div>
              Connect Outlook
            </button>
            <button onClick={handleConnectOutlookBrowser} style={{
              flex: 1, padding: '12px 16px', borderRadius: 8, border: `1px solid ${showBrowserLogin ? '#0078d4' : colors.border}`,
              background: showBrowserLogin ? '#f0f7ff' : '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontSize: 13, fontWeight: 600, color: '#0078d4', transition: 'all 0.2s'
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #0078d4, #005a9e)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faGlobe} style={{ color: '#fff', fontSize: 11 }} />
              </div>
              Connect via Browser
            </button>
            <button onClick={handleConnectGmail} style={{
              flex: 1, padding: '12px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontSize: 13, fontWeight: 600, color: '#ea4335', transition: 'all 0.2s'
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #ea4335, #4285f4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#fff', fontSize: 11 }} />
              </div>
              Connect Gmail
            </button>
          </div>

          {/* Browser connection info */}
          <div style={{ fontSize: 11, color: '#888', marginTop: 8, fontStyle: 'italic' }}>
            <strong>Connect via Browser</strong> — Use this if your organization's IT policy blocks the standard Outlook connection. Signs in directly through Outlook Web.
          </div>

          {/* Browser login — noVNC remote browser */}
          {showBrowserLogin && (
            <div style={{
              marginTop: 16, borderRadius: 10, overflow: 'hidden',
              border: `2px solid #0078d4`, background: '#000'
            }}>
              <div style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #0078d4, #005a9e)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faGlobe} className="me-2" />
                  Secure Browser — Log into your Outlook below
                </div>
                <button onClick={() => setShowBrowserLogin(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 }}>&times;</button>
              </div>
              <iframe
                src={`${window.location.protocol}//${window.location.hostname}/vnc/vnc.html?autoconnect=true&resize=scale&path=vnc/websockify`}
                style={{ width: '100%', height: 520, border: 'none' }}
                title="Outlook Browser Login"
              />
              <div style={{ padding: '8px 16px', background: '#f0f4f8', fontSize: 11, color: '#666', lineHeight: 1.5 }}>
                <strong>Instructions:</strong> Log into your Outlook account above. Once you see your inbox, close this panel — your session is saved.
                Email syncing will happen automatically. You won't need to log in again for 30-90 days.
              </div>
            </div>
          )}

          {/* Scan for contacts button */}
          {connectedEmails.length > 0 && (
            <button onClick={startDiscoveryPolling} style={{
              width: '100%', padding: '12px', borderRadius: 8, marginTop: 12,
              background: `linear-gradient(135deg, ${colors.navy}, #2a4a7f)`,
              color: '#fff', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600
            }}>
              <FontAwesomeIcon icon={faUsers} className="me-2" />
              Scan Email for {isLender ? 'Borrower' : 'Client'} Contacts
            </button>
          )}

          {/* Privacy note */}
          <div style={{ fontSize: 12, color: '#999', marginTop: 12, lineHeight: 1.5 }}>
            <strong>Privacy:</strong> We only access emails involving your {isLender ? 'borrowers and transaction parties' : 'CRM clients'}. Your full inbox is never read or stored.
          </div>
        </>
      )}

      {/* Discovery Selection Modal — same as agent portal */}
      {showDiscovery && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => !discoveryLoading && setShowDiscovery(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, width: '90%', maxWidth: 600,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: `1px solid ${colors.border}`,
              background: colors.navy, borderRadius: '16px 16px 0 0'
            }}>
              <h4 style={{ margin: 0, color: '#fff', fontSize: 18, fontFamily: fonts.display }}>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: colors.gold }} />
                {isLender ? 'Borrower & Transaction Contact Discovery' : 'Client Contact Discovery'}
              </h4>
              <p style={{ margin: '4px 0 0', color: '#aaa', fontSize: 13 }}>
                {isLender
                  ? 'Select borrowers and transaction contacts to import from your email'
                  : 'Select clients to import to your CRM'
                }
              </p>
            </div>

            {/* Body */}
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {discoveryLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ color: colors.gold, fontSize: 32 }} />
                  <p style={{ marginTop: 16, color: '#666' }}>Scanning your sent emails for contacts...</p>
                  <div style={{ width: '60%', margin: '12px auto', background: '#e9ecef', borderRadius: 8, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${discoveryProgress}%`, background: `linear-gradient(135deg, ${colors.gold}, #E8B44A)`, height: '100%', borderRadius: 8, transition: 'width 0.5s ease' }} />
                  </div>
                  <small style={{ color: '#999' }}>{discoveryProgress}% complete — this runs in the background</small>
                </div>
              ) : discoveredContacts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FontAwesomeIcon icon={faUsers} size="3x" style={{ color: '#ccc', marginBottom: 16 }} />
                  <h5 style={{ color: '#555' }}>No new contacts found</h5>
                  <p style={{ color: '#999' }}>All your email contacts are already imported, or no {isLender ? 'borrower' : 'real estate'} conversations were found.</p>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <h6 style={{ fontWeight: 700, margin: 0, color: colors.navy }}>
                        We found {discoveredContacts.length} potential {isLender ? 'contacts' : 'clients'} in your email
                      </h6>
                      <small style={{ color: '#888' }}>Select the ones you'd like to import</small>
                    </div>
                    <button onClick={() => {
                      if (selectedDiscovered.size === discoveredContacts.length) {
                        setSelectedDiscovered(new Set());
                      } else {
                        setSelectedDiscovered(new Set(discoveredContacts.map((c: any) => c.email)));
                      }
                    }} style={{
                      background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6,
                      padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: colors.navy
                    }}>
                      {selectedDiscovered.size === discoveredContacts.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  {discoveredContacts.map((contact: any) => (
                    <div key={contact.email}
                      onClick={() => {
                        const newSet = new Set(selectedDiscovered);
                        if (newSet.has(contact.email)) newSet.delete(contact.email);
                        else newSet.add(contact.email);
                        setSelectedDiscovered(newSet);
                      }}
                      style={{
                        padding: '12px 16px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
                        background: selectedDiscovered.has(contact.email) ? '#fff8e1' : '#f8f9fa',
                        border: `1px solid ${selectedDiscovered.has(contact.email) ? '#ffe082' : '#e9ecef'}`,
                        transition: 'all 0.15s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input type="checkbox" checked={selectedDiscovered.has(contact.email)} readOnly
                            style={{ accentColor: colors.gold, width: 18, height: 18 }} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                              {contact.name || contact.email.split('@')[0]}
                            </div>
                            <div style={{ fontSize: 12, color: '#888' }}>{contact.email}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: colors.gold }}>{contact.emailCount || contact.count} emails</div>
                          {contact.lastSubject || contact.subject ? (
                            <div style={{ fontSize: 11, color: '#aaa', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {contact.lastSubject || contact.subject}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Footer */}
            {discoveredContacts.length > 0 && !discoveryLoading && (
              <div style={{
                padding: '16px 24px', borderTop: `1px solid ${colors.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: '#f8f9fa', borderRadius: '0 0 16px 16px'
              }}>
                <small style={{ color: '#888' }}>{selectedDiscovered.size} of {discoveredContacts.length} selected</small>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowDiscovery(false)} style={{
                    padding: '8px 16px', borderRadius: 6, border: `1px solid ${colors.border}`,
                    background: '#fff', cursor: 'pointer', fontSize: 13, color: '#666'
                  }}>Skip for Now</button>
                  <button
                    disabled={selectedDiscovered.size === 0 || importingLeads}
                    onClick={handleImportSelected}
                    style={{
                      padding: '8px 20px', borderRadius: 6, border: 'none',
                      background: selectedDiscovered.size === 0 ? '#ccc' : `linear-gradient(135deg, ${colors.gold}, #b8860b)`,
                      color: '#fff', cursor: selectedDiscovered.size === 0 ? 'not-allowed' : 'pointer',
                      fontSize: 13, fontWeight: 700
                    }}
                  >
                    {importingLeads ? (
                      <><FontAwesomeIcon icon={faSpinner} spin className="me-2" />Importing...</>
                    ) : (
                      <>Import {selectedDiscovered.size} {isLender ? 'Contacts' : 'Clients'}</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lender Settings Tab
// ---------------------------------------------------------------------------

const LenderSettingsTab: React.FC<{ sponsorType: string }> = ({ sponsorType }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    nmlsId: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    companyPhone: '',
    website: '',
    title: '',
  });

  // Load existing profile data
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get('/auth/me');
        if (res.data) {
          const u = res.data;
          setFormData(prev => ({
            ...prev,
            firstName: u.user_first_name || '',
            lastName: u.user_last_name || '',
            email: u.user_email || '',
            phone: u.user_phone || '',
            title: u.user_title || '',
          }));
          if (u.profile_photo) setProfilePhoto(u.profile_photo);
        }

        // Load portal settings for company info
        const portalRes = await axiosInstance.get('/onboarding/status');
        if (portalRes.data?.success && portalRes.data?.data?.portalSettings) {
          const ps = portalRes.data.data.portalSettings;
          setFormData(prev => ({
            ...prev,
            companyName: ps.brokerage_name || prev.companyName,
            companyPhone: ps.brokerage_phone || prev.companyPhone,
            companyAddress: ps.brokerage_address || prev.companyAddress,
            website: ps.website_url || prev.website,
            nmlsId: ps.nmls_id || prev.nmlsId,
          }));
          if (ps.brokerage_logo) setCompanyLogo(ps.brokerage_logo);
        }
      } catch (err) {
        console.warn('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update user profile
      const profilePayload = new FormData();
      profilePayload.append('user_first_name', formData.firstName);
      profilePayload.append('user_last_name', formData.lastName);
      profilePayload.append('user_phone', formData.phone);
      profilePayload.append('user_title', formData.title);
      await axiosInstance.put('/auth/update-profile', profilePayload);

      // Update portal settings with lender-specific fields
      await axiosInstance.put('/portal/settings', {
        brokerage_name: formData.companyName,
        brokerage_phone: formData.companyPhone,
        brokerage_address: formData.companyAddress,
        website_url: formData.website,
        nmls_id: formData.nmlsId,
        company_city: formData.companyCity,
        company_state: formData.companyState,
        company_zip: formData.companyZip,
      });

      toast.success('Settings saved successfully');
    } catch (err: any) {
      console.error('Save failed:', err);
      toast.error(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (files: File[]) => {
    if (!files.length) return;
    const fd = new FormData();
    fd.append('attachments', files[0]);
    try {
      const res = await axiosInstance.put('/auth/update-profile', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.user?.profile_photo) {
        setProfilePhoto(res.data.user.profile_photo);
        toast.success('Profile photo updated');
      }
    } catch (err) {
      toast.error('Failed to upload photo');
    }
  };

  const handleLogoUpload = async (files: File[]) => {
    if (!files.length) return;
    const fd = new FormData();
    fd.append('brokerage_logo', files[0]);
    try {
      const res = await axiosInstance.put('/portal/settings', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.settings?.brokerage_logo) {
        setCompanyLogo(res.data.settings.brokerage_logo);
      }
      toast.success('Company logo updated');
    } catch (err) {
      toast.error('Failed to upload logo');
    }
  };

  const isLender = sponsorType === 'lender';
  const companyLabel = isLender ? 'Lending Company' : 'Company';

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: colors.gold }} />
        <p style={{ marginTop: 16, color: '#888' }}>Loading settings...</p>
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    background: '#f8f9fc',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    width: '100%',
    color: '#333',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: colors.navy,
    marginBottom: 6,
    display: 'block',
    fontFamily: fonts.body,
  };

  return (
    <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: 0 }}>
          Settings
        </h2>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: `linear-gradient(135deg, ${colors.gold}, #b8860b)`,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 28px',
            fontSize: 14,
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? (
            <><FontAwesomeIcon icon={faSpinner} spin className="me-2" />Saving...</>
          ) : (
            <>Save Changes</>
          )}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left Column — Personal Info */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: `1px solid ${colors.border}` }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 20px', fontFamily: fonts.body }}>
            <FontAwesomeIcon icon={faUsers} className="me-2" style={{ color: colors.gold }} />
            Personal Information
          </h3>

          {/* Profile Photo */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {profilePhoto ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={profilePhoto}
                  alt="Profile"
                  style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${colors.gold}` }}
                />
                <Dropzone onDrop={handlePhotoUpload} accept={{ 'image/*': ['.jpg', '.jpeg', '.png'] }}>
                  <div style={{ marginTop: 8, fontSize: 12, color: colors.gold, cursor: 'pointer' }}>
                    Change Photo
                  </div>
                </Dropzone>
              </div>
            ) : (
              <Dropzone onDrop={handlePhotoUpload} accept={{ 'image/*': ['.jpg', '.jpeg', '.png'] }}>
                <div style={{
                  width: 100, height: 100, borderRadius: '50%', background: '#f0f0f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto', cursor: 'pointer', border: `2px dashed ${colors.border}`
                }}>
                  <img src={imageIcon} alt="Upload" style={{ width: 32, opacity: 0.4 }} />
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>Upload Profile Photo</div>
              </Dropzone>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>First Name</label>
              <input style={inputStyle} value={formData.firstName}
                onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input style={inputStyle} value={formData.lastName}
                onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))} />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Title</label>
            <input style={inputStyle} value={formData.title} placeholder={isLender ? 'e.g. Loan Officer, Branch Manager' : 'e.g. Director, VP'}
              onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Email</label>
            <input style={{ ...inputStyle, background: '#eee', cursor: 'not-allowed' }} value={formData.email} disabled />
            <span style={{ fontSize: 11, color: '#999' }}>Contact support to change email</span>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} value={formData.phone}
              onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} />
          </div>

          {isLender && (
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>NMLS ID #</label>
              <input style={inputStyle} value={formData.nmlsId} placeholder="e.g. 123456"
                onChange={e => setFormData(p => ({ ...p, nmlsId: e.target.value }))} />
              <span style={{ fontSize: 11, color: '#999' }}>Required for marketing compliance</span>
            </div>
          )}
        </div>

        {/* Right Column — Company Info */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: `1px solid ${colors.border}` }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 20px', fontFamily: fonts.body }}>
            <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: colors.gold }} />
            {companyLabel} Information
          </h3>

          {/* Company Logo */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {companyLogo ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={companyLogo}
                  alt="Company Logo"
                  style={{ maxWidth: 200, maxHeight: 80, objectFit: 'contain', border: `1px solid ${colors.border}`, borderRadius: 8, padding: 8 }}
                />
                <Dropzone onDrop={handleLogoUpload} accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.svg'] }}>
                  <div style={{ marginTop: 8, fontSize: 12, color: colors.gold, cursor: 'pointer' }}>
                    Change Logo
                  </div>
                </Dropzone>
              </div>
            ) : (
              <Dropzone onDrop={handleLogoUpload} accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.svg'] }}>
                <div style={{
                  width: 200, height: 80, background: '#f0f0f0', borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto', cursor: 'pointer', border: `2px dashed ${colors.border}`
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <img src={imageIcon} alt="Upload" style={{ width: 24, opacity: 0.4 }} />
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Upload {companyLabel} Logo</div>
                  </div>
                </div>
              </Dropzone>
            )}
          </div>

          <div style={{ marginTop: 0 }}>
            <label style={labelStyle}>{companyLabel} Name</label>
            <input style={inputStyle} value={formData.companyName}
              placeholder={isLender ? 'e.g. Neighborhood Home Loans' : 'e.g. ABC Title Company'}
              onChange={e => setFormData(p => ({ ...p, companyName: e.target.value }))} />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>{companyLabel} Address</label>
            <input style={inputStyle} value={formData.companyAddress} placeholder="Street address"
              onChange={e => setFormData(p => ({ ...p, companyAddress: e.target.value }))} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginTop: 12 }}>
            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} value={formData.companyCity}
                onChange={e => setFormData(p => ({ ...p, companyCity: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input style={inputStyle} value={formData.companyState} placeholder="AZ"
                onChange={e => setFormData(p => ({ ...p, companyState: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Zip</label>
              <input style={inputStyle} value={formData.companyZip}
                onChange={e => setFormData(p => ({ ...p, companyZip: e.target.value }))} />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>{companyLabel} Phone</label>
            <input style={inputStyle} value={formData.companyPhone}
              onChange={e => setFormData(p => ({ ...p, companyPhone: e.target.value }))} />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Website</label>
            <input style={inputStyle} value={formData.website} placeholder="https://"
              onChange={e => setFormData(p => ({ ...p, website: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Email Connectivity */}
      <LenderEmailConnectivity isLender={isLender} />

      {/* Info Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(201, 168, 76, 0.1), rgba(201, 168, 76, 0.05))',
        borderRadius: 12, padding: 16, marginTop: 24,
        border: `1px solid rgba(201, 168, 76, 0.3)`,
        display: 'flex', alignItems: 'center', gap: 12
      }}>
        <FontAwesomeIcon icon={faShieldAlt} style={{ color: colors.gold, fontSize: 20 }} />
        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>
          {isLender ? (
            <>Your profile photo, company logo, and NMLS ID are displayed on sponsored agent portals and marketing materials. Ensure your NMLS ID is current for regulatory compliance.</>
          ) : (
            <>Your profile photo and company logo are displayed on sponsored agent portals and co-branded materials.</>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SponsorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  const [agents, setAgents] = useState<SponsoredAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<SponsoredAgent | null>(null);

  // Coaching state — read agent from URL if present
  const [coachAgent, setCoachAgent] = useState<string | null>(() => {
    const params = new URLSearchParams(location.search);
    return params.get('agentId') || null;
  });
  const [coachData, setCoachData] = useState<any>(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachPrompts, setCoachPrompts] = useState<string[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [coachChat, setCoachChat] = useState<Array<{ role: string; text: string }>>([]);
  const [coachChatInput, setCoachChatInput] = useState('');
  const [coachChatLoading, setCoachChatLoading] = useState(false);
  const [sellScores, setSellScores] = useState<any[]>([]);
  const [retentionDeals, setRetentionDeals] = useState<any>(null);
  const [pastClosings, setPastClosings] = useState<any>(null);

  // Sponsor type — loaded from user's brokerage metadata
  const [sponsorType, setSponsorType] = useState<string>('lender');
  const sponsorConfig = SPONSOR_TYPE_CONFIG[sponsorType] || SPONSOR_TYPE_CONFIG.vendor;
  const tabs = useMemo(() => getTabsForType(sponsorType), [sponsorType]);

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [showInviteAdmin, setShowInviteAdmin] = useState(false);
  const [inviteData, setInviteData] = useState({ firstName: '', lastName: '', email: '', phone: '', fundingModel: 'full_sponsor', sponsorPct: '100' });
  const [adminInviteData, setAdminInviteData] = useState({ name: '', email: '' });
  const [inviting, setInviting] = useState(false);
  const [showEmailImport, setShowEmailImport] = useState(false);
  const [emailImportText, setEmailImportText] = useState('');
  const [emailImportLoading, setEmailImportLoading] = useState(false);
  const [reportDownloading, setReportDownloading] = useState<string | null>(null);
  const [emailImportResults, setEmailImportResults] = useState<any>(null);

  const [brokerageId, setBrokerageId] = useState('');
  const [totalSeats, setTotalSeats] = useState(50);

  // KOPS-642: Team management state
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamInviteData, setTeamInviteData] = useState({ name: '', email: '', phone: '' });
  const [teamInviting, setTeamInviting] = useState(false);
  const [showTeamInvite, setShowTeamInvite] = useState(false);

  const [plannerData, setPlannerData] = useState<any>(null);
  const [plannerLoading, setPlannerLoading] = useState(false);
  const [automationsData, setAutomationsData] = useState<any>(null);
  const [automationsLoading, setAutomationsLoading] = useState(false);
  const [queueData, setQueueData] = useState<any>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [sponsorEmail, setSponsorEmail] = useState('');

  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    if (tab !== activeTab) setActiveTab(tab);
  }, [location.pathname]);

  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key);
    const basePath = location.pathname.includes('/sponsor') ? '/sponsor' : '/lender';
    navigate(`${basePath}/${key}`, { replace: true });
  }, [navigate]);

  // Fetch sponsored agents
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/lender/agents');
      if (res.data.success) {
        setAgents(res.data.agents || []);
        if (res.data.brokerageId) setBrokerageId(res.data.brokerageId);
        if (res.data.totalSeats) setTotalSeats(res.data.totalSeats);
        if (res.data.sponsorType) setSponsorType(res.data.sponsorType);
        if (res.data.sponsorEmail) setSponsorEmail(res.data.sponsorEmail);
      }
    } catch (err: any) {
      console.error('Failed to load sponsored agents:', err);
      // Fallback
      try {
        const brokerageRes = await axiosInstance.get('/chat/team/members');
        if (brokerageRes.data.brokerage?.id) {
          setBrokerageId(brokerageRes.data.brokerage.id);
          const agentsRes = await axiosInstance.get(`/broker/agents/${brokerageRes.data.brokerage.id}`);
          if (agentsRes.data.success) {
            setAgents(agentsRes.data.agents.map((a: any) => ({
              id: a.id,
              userId: a.user_id,
              name: a.agent_full_name?.replace(' (Pending Invitation)', '') || '',
              email: a.agent_email,
              phone: a.agent_phone || '',
              status: a.isAuthenticated ? 'active' : 'pending',
              respaAcknowledged: false,
            })));
          }
        }
      } catch {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // KOPS-642: Fetch team members
  const fetchTeamMembers = useCallback(async () => {
    setTeamLoading(true);
    try {
      const res = await axiosInstance.get('/sponsor/team/members');
      if (res.data.success) setTeamMembers(res.data.members || []);
    } catch (err) { console.error('Failed to load team:', err); }
    setTeamLoading(false);
  }, []);

  useEffect(() => { if (activeTab === 'team') fetchTeamMembers(); }, [activeTab, fetchTeamMembers]);

  const handleTeamInvite = useCallback(async () => {
    if (!teamInviteData.name || !teamInviteData.email) { toast.error('Name and email are required'); return; }
    setTeamInviting(true);
    try {
      const res = await axiosInstance.post('/sponsor/team/invite', { ...teamInviteData, brokerageId });
      if (res.data.success) {
        toast.success(res.data.message || 'Invitation sent');
        setTeamInviteData({ name: '', email: '', phone: '' });
        setShowTeamInvite(false);
        fetchTeamMembers();
      }
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to invite'); }
    setTeamInviting(false);
  }, [teamInviteData, brokerageId, fetchTeamMembers]);

  const handleRemoveTeamMember = useCallback(async (memberId: string, memberName: string) => {
    if (!window.confirm(`Remove ${memberName} from the team? They will lose access to this dashboard.`)) return;
    try {
      const res = await axiosInstance.delete(`/sponsor/team/members/${memberId}`);
      if (res.data.success) { toast.success('Team member removed'); fetchTeamMembers(); }
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to remove'); }
  }, [fetchTeamMembers]);

  // Auto-load coaching from URL agentId param
  useEffect(() => {
    if (coachAgent && agents.length > 0 && !coachData && activeTab === 'coaching') {
      (async () => {
        setCoachLoading(true);
        try {
          const { data } = await axiosInstance.get(`/sponsor/coaching/${coachAgent}`);
          if (data.success) {
            setCoachData(data);
            // Auto-load retention deals + past closings if MLS ID found
            if (data.agent?.mlsId) {
              axiosInstance.get(`/broker/retention/agent/${data.agent.mlsId}/lost-deals`).then(({ data: retData }) => {
                if (retData.success) setRetentionDeals(retData);
              }).catch(() => {});
            }
            // Past closings via contacts mls-production endpoint
            if (data.agent?.contactId) {
              axiosInstance.get(`/contacts/${data.agent.contactId}/mls-production`).then(({ data: mlsData }) => {
                if (mlsData.success && mlsData.mlsProduction) setPastClosings(mlsData.mlsProduction);
              }).catch(() => {});
            }
          }
          const { data: promptData } = await axiosInstance.get(`/sponsor/coaching/${coachAgent}/prompts`);
          if (promptData.success && promptData.prompts?.length > 0) setCoachPrompts(promptData.prompts);
        } catch { }
        setCoachLoading(false);
      })();
    }
  }, [coachAgent, agents, activeTab]);

  // Invite agent
  const handleInvite = useCallback(async () => {
    if (!inviteData.email || !inviteData.firstName) {
      toast.error('Name and email are required');
      return;
    }
    if (agents.length >= totalSeats) {
      toast.error(`All ${totalSeats} seats are in use. Contact sales to add more seats.`);
      return;
    }
    setInviting(true);
    try {
      const res = await axiosInstance.post('/lender/save-agent', {
        agentData: inviteData,
        brokerageId,
      });
      if (res.data.success) {
        toast.success('Agent added! You can send the invitation from their profile.');
        setInviteData({ firstName: '', lastName: '', email: '', phone: '', fundingModel: 'full_sponsor', sponsorPct: '100' });
        setShowInvite(false);
        fetchAgents();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to invite agent');
    } finally {
      setInviting(false);
    }
  }, [inviteData, brokerageId, agents.length, totalSeats, fetchAgents]);

  const handleRemove = useCallback(async (agentId: string) => {
    try {
      await axiosInstance.delete(`/lender/agent/${agentId}`);
      toast.success('Agent removed — seat freed');
      setSelectedAgent(null);
      fetchAgents();
    } catch {
      toast.error('Failed to remove agent');
    }
  }, [fetchAgents]);

  // Export CSV
  const handleExportCSV = useCallback(() => {
    const headers = 'Agent Name,Email,Status,RESPA Acknowledged,Acknowledged Date,Agreement Version\n';
    const rows = agents.map(a =>
      `"${a.name}","${a.email}","${a.status}","${a.respaAcknowledged ? 'Yes' : 'No'}","${a.respaAcknowledgedAt || ''}","1.0"`
    ).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `respa_compliance_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [agents]);

  // Stats
  const stats = useMemo(() => {
    const active = agents.filter(a => a.status === 'active').length;
    const pending = agents.filter(a => a.status !== 'active').length;
    const acknowledged = agents.filter(a => a.respaAcknowledged).length;
    const complianceRate = agents.length > 0 ? Math.round((acknowledged / agents.length) * 100) : 0;
    return { total: agents.length, active, pending, seatsAvailable: totalSeats - agents.length, acknowledged, complianceRate };
  }, [agents, totalSeats]);

  const filteredAgents = useMemo(() => {
    if (!search) return agents;
    const q = search.toLowerCase();
    return agents.filter(a => (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q));
  }, [agents, search]);

  const metrics: HubMetric[] = useMemo(() => [
    { icon: faUsers, value: `${stats.total}/${totalSeats}`, label: 'Seats Used', color: colors.blue },
    { icon: faCheckCircle, value: stats.active, label: 'Active Agents', color: colors.green },
    { icon: faClockRotateLeft, value: stats.seatsAvailable, label: 'Available', color: colors.gold2 },
    { icon: faShieldAlt, value: `${stats.complianceRate}%`, label: 'RESPA Compliance', color: stats.complianceRate === 100 ? colors.green : colors.orange },
  ], [stats, totalSeats]);

  const breadcrumb = `${sponsorConfig.label.replace(' Dashboard', '')} / ${tabs.find(t => t.key === activeTab)?.label || 'Dashboard'}`;

  // Agents not yet acknowledged
  const outstandingRespa = agents.filter(a => !a.respaAcknowledged && a.status !== 'inactive');

  return (
    <HubShell
      breadcrumb={breadcrumb}
      brandMiddle={sponsorConfig.brandMiddle}
      homeHref="/agent-portal/dashboard"
      metrics={activeTab === 'dashboard' ? metrics : []}
      rightNavContent={
        // KOPS-654: Pipeline Management is a lender feature. For a brokerage account, surface the
        // Broker Intelligence Hub instead (identified by sponsorType from /admin/sponsors).
        sponsorType === 'brokerage' ? (
          <a href="/broker-intelligence" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
            color: colors.navy, border: 'none', borderRadius: 6,
            padding: '8px 14px', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.5px', textTransform: 'uppercase',
            textDecoration: 'none', cursor: 'pointer',
          }}>
            <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 13 }} />
            Broker Intelligence Hub
          </a>
        ) : (
          <a href="/lender-portal/pipeline?mode=pipeline" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
            color: colors.navy, border: 'none', borderRadius: 6,
            padding: '8px 14px', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.5px', textTransform: 'uppercase',
            textDecoration: 'none', cursor: 'pointer',
          }}>
            <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 13 }} />
            Pipeline Management
          </a>
        )
      }
      actions={
        <GoldButton onClick={() => { setShowInvite(true); setActiveTab('agents'); navigate('/lender/agents', { replace: true }); }}>
          <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: 6 }} /> Add Agent
        </GoldButton>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      {/* ── Dashboard ──────────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: '0 0 24px' }}>
            {sponsorConfig.label}
          </h2>

          {/* Seat utilization */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <div style={{ flex: 1, background: '#fff', borderRadius: 12, padding: 24, border: `1px solid ${colors.border}` }}>
              <h3 style={{ fontSize: 14, color: colors.muted, margin: '0 0 12px', fontFamily: fonts.body }}>Seat Utilization</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: `conic-gradient(${colors.gold} ${(stats.total / totalSeats) * 360}deg, ${colors.border} 0deg)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 60, height: 60, borderRadius: '50%', background: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 16, color: colors.navy,
                  }}>
                    {Math.round((stats.total / totalSeats) * 100)}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 14, color: colors.navy, fontWeight: 600 }}>{stats.total} of {totalSeats} seats used</div>
                  <div style={{ fontSize: 12, color: colors.muted }}>{stats.seatsAvailable} seats available</div>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, background: '#fff', borderRadius: 12, padding: 24, border: `1px solid ${colors.border}` }}>
              <h3 style={{ fontSize: 14, color: colors.muted, margin: '0 0 12px', fontFamily: fonts.body }}>RESPA Compliance</h3>
              <div style={{ fontSize: 32, fontWeight: 700, color: stats.complianceRate === 100 ? colors.green : colors.orange }}>
                {stats.complianceRate}%
              </div>
              <div style={{ fontSize: 12, color: colors.muted }}>
                {stats.acknowledged} of {stats.total} agents acknowledged
              </div>
              {outstandingRespa.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: colors.orange }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 4 }} />
                  {outstandingRespa.length} agent(s) pending RESPA acknowledgment
                </div>
              )}
            </div>
          </div>

          {/* RESPA notice */}
          <div style={{
            background: 'rgba(201,149,42,0.1)', border: `1px solid ${colors.gold}`,
            borderRadius: 8, padding: 16, fontSize: 13, color: colors.gold, lineHeight: 1.6,
          }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 6 }} />
            <strong>Compliance Notice:</strong> Per KairosIQ's Lender Seat Licensing Policy, all sponsored
            agents must acknowledge the RESPA disclosure before their accounts are activated. No referral
            obligations exist. Software access only.
          </div>
        </div>
      )}

      {/* ── Sponsored Agents ───────────────────────────────────────── */}
      {activeTab === 'agents' && (
        <SplitPanel
          selectedItem={!!selectedAgent}
          onBack={() => setSelectedAgent(null)}
          left={
            <>
              <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <SearchBar value={search} onChange={setSearch} placeholder="Search agents..." />
                  <button
                    onClick={() => setShowInvite(!showInvite)}
                    disabled={agents.length >= totalSeats}
                    style={{
                      background: agents.length >= totalSeats ? colors.muted : `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                      border: 'none', borderRadius: 6, padding: '0 12px',
                      color: colors.navy, fontWeight: 700, fontSize: 12, cursor: agents.length >= totalSeats ? 'default' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>

                {/* Action Buttons Row */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  {/* KOPS-654: brokerage accounts get the Broker Intelligence Hub instead of the lender Pipeline */}
                  <button
                    onClick={() => window.location.href = sponsorType === 'brokerage' ? '/broker-intelligence' : '/lender-portal/pipeline?mode=pipeline'}
                    style={{ flex: 1, background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 6, padding: '6px 8px',
                      color: colors.navy, fontWeight: 700, fontSize: 11, cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={faChartLine} /> {sponsorType === 'brokerage' ? 'Broker Intelligence Hub' : 'Pipeline Management'}
                  </button>
                  <button
                    onClick={() => { setShowInviteAdmin(!showInviteAdmin); setShowInvite(false); }}
                    style={{ flex: 1, background: 'transparent', border: `1px solid ${colors.gold}`, borderRadius: 6, padding: '6px 8px',
                      color: colors.gold, fontWeight: 600, fontSize: 11, cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={faUserPlus} /> Invite Admin
                  </button>
                </div>

                {/* Invite Admin Form */}
                {showInviteAdmin && (
                  <div style={{ background: colors.navy3, borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: colors.muted, marginBottom: 8 }}>Admins get dashboard access (free — doesn't use a seat)</div>
                    <input
                      placeholder="Admin Name *"
                      value={adminInviteData.name}
                      onChange={e => setAdminInviteData(prev => ({ ...prev, name: e.target.value }))}
                      style={{ width: '100%', background: colors.navy2, border: 'none', borderRadius: 4, padding: '6px 10px', color: colors.white, fontSize: 12, marginBottom: 6, outline: 'none', fontFamily: fonts.body }}
                    />
                    <input
                      placeholder="Admin Email *"
                      type="email"
                      value={adminInviteData.email}
                      onChange={e => setAdminInviteData(prev => ({ ...prev, email: e.target.value }))}
                      style={{ width: '100%', background: colors.navy2, border: 'none', borderRadius: 4, padding: '6px 10px', color: colors.white, fontSize: 12, marginBottom: 6, outline: 'none', fontFamily: fonts.body }}
                    />
                    <button
                      onClick={async () => {
                        if (!adminInviteData.email || !adminInviteData.name) { toast.error('Name and email required'); return; }
                        try {
                          const { data } = await axiosInstance.post('/sponsor/invite-admin', { ...adminInviteData, brokerageId });
                          if (data.success) { toast.success(`Admin invite sent to ${adminInviteData.email}`); setAdminInviteData({ name: '', email: '' }); setShowInviteAdmin(false); }
                          else toast.error(data.error || 'Failed');
                        } catch { toast.error('Failed to invite admin'); }
                      }}
                      style={{ width: '100%', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 6, padding: '8px', color: colors.navy, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                    >
                      Send Admin Invite
                    </button>
                  </div>
                )}

                {agents.length >= totalSeats && (
                  <div style={{ fontSize: 11, color: colors.orange, marginBottom: 6 }}>
                    All {totalSeats} seats are in use.
                  </div>
                )}

                {showInvite && (
                  <div style={{ background: colors.navy3, borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    {['firstName', 'lastName', 'email', 'phone'].map(field => (
                      <input
                        key={field}
                        type={field === 'email' ? 'email' : 'text'}
                        placeholder={field === 'firstName' ? 'First Name *' : field === 'lastName' ? 'Last Name' : field === 'email' ? 'Email *' : 'Phone'}
                        value={(inviteData as any)[field]}
                        onChange={e => setInviteData(prev => ({ ...prev, [field]: e.target.value }))}
                        style={{
                          width: '100%', background: colors.navy2, border: 'none',
                          borderRadius: 4, padding: '6px 10px', color: colors.white,
                          fontSize: 12, marginBottom: 6, outline: 'none', fontFamily: fonts.body,
                        }}
                      />
                    ))}
                    {/* Funding Model */}
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 10, color: colors.muted, marginBottom: 4 }}>Who pays for this seat?</div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[
                          { value: 'full_sponsor', label: 'I Pay 100%' },
                          { value: 'split', label: 'Split Cost' },
                          { value: 'agent_pays', label: 'Agent Pays' },
                        ].map(opt => (
                          <button key={opt.value}
                            onClick={() => setInviteData(prev => ({ ...prev, fundingModel: opt.value, sponsorPct: opt.value === 'full_sponsor' ? '100' : opt.value === 'agent_pays' ? '0' : prev.sponsorPct }))}
                            style={{
                              flex: 1, padding: '5px 4px', fontSize: 10, fontWeight: 600, borderRadius: 4, cursor: 'pointer',
                              border: inviteData.fundingModel === opt.value ? `1px solid ${colors.gold}` : '1px solid transparent',
                              background: inviteData.fundingModel === opt.value ? 'rgba(201,149,42,0.2)' : colors.navy2,
                              color: inviteData.fundingModel === opt.value ? colors.gold : colors.muted,
                            }}
                          >{opt.label}</button>
                        ))}
                      </div>
                    </div>
                    {inviteData.fundingModel === 'split' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 10, color: colors.muted, whiteSpace: 'nowrap' }}>You pay</span>
                        <input type="number" min="1" max="99" value={inviteData.sponsorPct}
                          onChange={e => setInviteData(prev => ({ ...prev, sponsorPct: e.target.value }))}
                          style={{ width: 50, background: colors.navy2, border: 'none', borderRadius: 4, padding: '4px 6px', color: colors.white, fontSize: 12, textAlign: 'center', outline: 'none' }}
                        />
                        <span style={{ fontSize: 10, color: colors.muted }}>% (${Math.round(185 * (parseInt(inviteData.sponsorPct) || 0) / 100)}/mo)</span>
                        <span style={{ fontSize: 10, color: colors.gold, marginLeft: 'auto' }}>Agent pays ${Math.round(185 * (1 - (parseInt(inviteData.sponsorPct) || 0) / 100))}/mo</span>
                      </div>
                    )}
                    {inviteData.fundingModel === 'agent_pays' && (
                      <div style={{ fontSize: 10, color: colors.muted, marginBottom: 6, padding: '4px 0' }}>
                        Agent will be asked to subscribe at $185/mo. No seat used from your allocation.
                      </div>
                    )}
                    {inviteData.fundingModel === 'full_sponsor' && agents.length >= totalSeats && (
                      <div style={{ fontSize: 10, color: colors.orange, marginBottom: 6 }}>All {totalSeats} seats in use. Add more seats in Billing tab.</div>
                    )}
                    <button
                      onClick={handleInvite}
                      disabled={inviting || (inviteData.fundingModel === 'full_sponsor' && agents.length >= totalSeats)}
                      style={{
                        width: '100%', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                        border: 'none', borderRadius: 6, padding: '8px',
                        color: colors.navy, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                        opacity: (inviting || (inviteData.fundingModel === 'full_sponsor' && agents.length >= totalSeats)) ? 0.5 : 1,
                      }}
                    >
                      {inviting ? <><FontAwesomeIcon icon={faSpinner} spin /> Adding...</> : 'Add Agent'}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ padding: 32, textAlign: 'center', color: colors.muted }}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                  </div>
                ) : filteredAgents.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                    <FontAwesomeIcon icon={faUsers} style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                    <div style={{ fontSize: 13 }}>
                      {agents.length === 0 ? 'No sponsored agents yet' : 'No matching agents'}
                    </div>
                  </div>
                ) : (
                  filteredAgents.map(agent => (
                    <ListItem
                      key={agent.id}
                      selected={selectedAgent?.id === agent.id}
                      onClick={() => setSelectedAgent(agent)}
                      title={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {agent.name}
                          <FontAwesomeIcon icon={faCircle} style={{
                            fontSize: 7,
                            color: agent.status === 'active' ? colors.green : colors.orange,
                          }} />
                        </span>
                      }
                      subtitle={agent.email}
                      rightContent={
                        <FontAwesomeIcon
                          icon={agent.respaAcknowledged ? faCheckCircle : faTimesCircle}
                          style={{
                            color: agent.respaAcknowledged ? colors.green : colors.red,
                            fontSize: 14,
                          }}
                          title={agent.respaAcknowledged ? 'RESPA Acknowledged' : 'RESPA Pending'}
                        />
                      }
                      meta={agent.respaAcknowledged ? 'RESPA: Acknowledged' : 'RESPA: Pending'}
                    />
                  ))
                )}
              </div>
            </>
          }
          right={
            selectedAgent ? (
              <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: colors.navy2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: colors.gold, fontWeight: 700, fontSize: 20,
                  }}>
                    {selectedAgent.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, color: colors.navy }}>{selectedAgent.name}</h3>
                    <p style={{ margin: 0, fontSize: 13, color: colors.muted }}>{selectedAgent.email}</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>Status</div>
                    <span style={{
                      fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                      background: selectedAgent.status === 'active' ? 'rgba(39,174,96,0.1)' : 'rgba(243,156,18,0.1)',
                      color: selectedAgent.status === 'active' ? colors.green : colors.orange,
                    }}>
                      {selectedAgent.status}
                    </span>
                  </div>
                  <div style={{ flex: 1, background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>RESPA</div>
                    <span style={{
                      fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                      background: selectedAgent.respaAcknowledged ? 'rgba(39,174,96,0.1)' : 'rgba(231,76,60,0.1)',
                      color: selectedAgent.respaAcknowledged ? colors.green : colors.red,
                    }}>
                      <FontAwesomeIcon icon={selectedAgent.respaAcknowledged ? faCheckCircle : faTimesCircle} style={{ marginRight: 4 }} />
                      {selectedAgent.respaAcknowledged ? 'Acknowledged' : 'Pending'}
                    </span>
                  </div>
                </div>

                {selectedAgent.respaAcknowledged && (
                  <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>Acknowledged By</div>
                    <div style={{ fontSize: 14, color: colors.navy }}>{selectedAgent.respaAgentTypedName || selectedAgent.name}</div>
                    <div style={{ fontSize: 11, color: colors.muted, marginTop: 2 }}>
                      {selectedAgent.respaAcknowledgedAt ? new Date(selectedAgent.respaAcknowledgedAt).toLocaleDateString() : ''}
                    </div>
                  </div>
                )}

                <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${colors.border}`, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>Billing</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: colors.navy }}>
                    {(selectedAgent as any).fundingLabel || 'Sponsor Pays'}
                  </div>
                  {(selectedAgent as any).fundingModel === 'split' && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      You: ${(selectedAgent as any).sponsorFee}/mo · Agent: ${(selectedAgent as any).agentFee}/mo
                    </div>
                  )}
                  {(selectedAgent as any).fundingModel === 'agent_pays' && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Agent pays $185/mo · No seat used
                    </div>
                  )}
                  {(!(selectedAgent as any).fundingModel || (selectedAgent as any).fundingModel === 'full_sponsor') && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      $185/mo per seat · You pay
                    </div>
                  )}
                </div>

                {selectedAgent.status !== 'active' && (
                  <GoldButton
                    onClick={async () => {
                      try {
                        const { data } = await axiosInstance.post('/sponsor/resend-invite', { memberId: selectedAgent.id });
                        if (data.success) {
                          toast.success(`Invitation sent to ${selectedAgent.email}`);
                          fetchAgents();
                        }
                        else toast.error(data.error || 'Failed to send');
                      } catch { toast.error('Failed to send invitation'); }
                    }}
                    style={{ width: '100%', marginBottom: 12 }}
                  >
                    <FontAwesomeIcon icon={selectedAgent.invitationSent ? faPaperPlane : faEnvelope} className="me-2" />
                    {selectedAgent.invitationSent ? 'Resend Invitation' : 'Send Invitation'}
                  </GoldButton>
                )}

                <OutlineButton onClick={() => handleRemove(selectedAgent.id)}>
                  Remove Agent (Free Seat)
                </OutlineButton>
              </div>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', height: '100%', padding: 40, textAlign: 'center',
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24,
                }}>
                  <FontAwesomeIcon icon={faUsers} style={{ fontSize: 36, color: colors.navy }} />
                </div>
                <h2 style={{ color: colors.navy, fontFamily: fonts.display, fontSize: 28, margin: '0 0 8px' }}>
                  Sponsored Agents
                </h2>
                <p style={{ color: colors.muted, fontSize: 14, maxWidth: 400, lineHeight: 1.6 }}>
                  Select an agent to view details and RESPA compliance status.
                  {stats.seatsAvailable > 0 && ` You have ${stats.seatsAvailable} seats available.`}
                </p>
              </div>
            )
          }
        />
      )}

      {/* ── RESPA Compliance ───────────────────────────────────────── */}
      {activeTab === 'planner' && (() => {
        if (!plannerData && !plannerLoading) { setPlannerLoading(true); axiosInstance.get('/sponsor/weekly-planner').then(({ data }) => { if (data.success) setPlannerData(data); }).catch(() => {}).finally(() => setPlannerLoading(false)); }
        if (plannerLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24, color: colors.gold }} /></div>;
        if (!plannerData) return <div style={{ padding: 24, color: colors.muted }}>Loading...</div>;
        const d = plannerData;
        return (<div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <div style={{ background: `linear-gradient(135deg, ${colors.navy}, #1a2d4a)`, borderRadius: 12, padding: 24, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div><div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase' }}>{d.dayOfWeek}'s Theme</div><div style={{ fontSize: 24, fontWeight: 700, color: '#fff', fontFamily: fonts.display }}>{d.theme.theme}</div><div style={{ fontSize: 13, color: '#8FA3BF', marginTop: 4 }}>{d.theme.focus}</div></div>
            <div style={{ textAlign: 'right' }}><div style={{ fontSize: 28, fontWeight: 700, color: colors.gold, fontFamily: fonts.display }}>{d.stats.totalAgents}</div><div style={{ fontSize: 11, color: '#8FA3BF' }}>Agents</div></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[{ label: 'Total Volume', value: '$' + Math.round(d.stats.totalVolume / 1000000) + 'M', color: '#27ae60' }, { label: 'Active', value: d.stats.activeAgents, color: '#2980b9' }, { label: 'Dormant', value: d.stats.dormantAgents, color: '#e67e22' }, { label: 'Lost Revenue', value: '$' + d.stats.totalLostRevenue.toLocaleString(), color: '#c62828' }].map((s, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 10, padding: 16, borderTop: `3px solid ${s.color}` }}><div style={{ fontSize: 22, fontWeight: 700, color: colors.navy }}>{s.value}</div><div style={{ fontSize: 11, color: '#888' }}>{s.label}</div></div>))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}><h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px' }}>📞 Who to Call Today</h4>
              {(d.callList || []).map((a: any, i: number) => (<div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < d.callList.length - 1 ? '1px solid #f0f0f0' : 'none' }}><div><div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{a.name}</div><div style={{ fontSize: 10, color: '#888' }}>{a.reasons?.[0]}</div></div><span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: a.callType === 'urgent' ? '#ffebee' : a.callType === 'important' ? '#fff3e0' : '#e8f5e9', color: a.callType === 'urgent' ? '#c62828' : a.callType === 'important' ? '#e65100' : '#2e7d32' }}>{(a.callType || '').toUpperCase()}</span></div>))}</div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}><h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px' }}>⭐ Top Producers</h4>
              {(d.whales || []).map((a: any, i: number) => (<div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < d.whales.length - 1 ? '1px solid #f0f0f0' : 'none' }}><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 10, fontWeight: 700, width: 20, textAlign: 'center', color: a.tier === 'platinum' ? colors.gold : '#888' }}>#{a.rank}</span><div><div style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{a.name}</div><div style={{ fontSize: 10, color: '#888' }}>{a.deals} deals · ${Math.round(a.volume / 1000000 * 10) / 10}M</div></div></div><span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8, background: a.status === 'active' ? '#e8f5e9' : a.status === 'cooling' ? '#fff3e0' : '#ffebee', color: a.status === 'active' ? '#2e7d32' : a.status === 'cooling' ? '#e65100' : '#c62828' }}>{a.status}</span></div>))}</div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}><h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px' }}>💌 Send Thank You</h4>
              {(d.thankYou || []).length === 0 && <div style={{ fontSize: 12, color: '#888' }}>No recent closings.</div>}
              {(d.thankYou || []).map((a: any, i: number) => (<div key={i} style={{ padding: '8px 0', borderBottom: i < d.thankYou.length - 1 ? '1px solid #f0f0f0' : 'none' }}><div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{a.name}</div><div style={{ fontSize: 10, color: '#27ae60' }}>{a.reason}</div></div>))}</div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}><h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px' }}>🔥 Reactivation Targets</h4>
              {(d.reactivate || []).length === 0 && <div style={{ fontSize: 12, color: '#888' }}>None.</div>}
              {(d.reactivate || []).map((a: any, i: number) => (<div key={i} style={{ padding: '8px 0', borderBottom: i < d.reactivate.length - 1 ? '1px solid #f0f0f0' : 'none' }}><div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{a.name}</div><div style={{ fontSize: 10, color: '#e67e22' }}>{a.reason}</div></div>))}</div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}`, gridColumn: '1 / -1' }}><h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px' }}>📊 Pipeline — {d.stats.totalPipelineLeads} Contacts</h4>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{Object.entries(d.stats.pipeline || {}).map(([stage, count]: [string, any]) => (<div key={stage} style={{ padding: '8px 16px', background: '#f8f9fa', borderRadius: 8, textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 700, color: colors.navy }}>{count}</div><div style={{ fontSize: 10, color: '#888' }}>{stage.replace(/_/g, ' ')}</div></div>))}</div></div>
          </div>
        </div>);
      })()}

      {activeTab === 'automations' && (() => {
        // Lazy-load automations data
        if (!automationsData && !automationsLoading) {
          setAutomationsLoading(true);
          axiosInstance.get('/ai-coach/upcoming').then(({ data }) => { if (data.success || data.tasks) setAutomationsData(data); }).catch(() => {}).finally(() => setAutomationsLoading(false));
        }
        if (!queueData && !queueLoading) {
          setQueueLoading(true);
          axiosInstance.get('/ai-coach/queue').then(({ data }) => { if (data.success || data.queue) setQueueData(data); }).catch(() => {}).finally(() => setQueueLoading(false));
        }
        if (automationsLoading || queueLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24, color: colors.gold }} /></div>;

        const allTasks = automationsData?.tasks || [];
        const now = new Date();
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        const next7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const todayQueue = allTasks.filter((t: any) => t.status === 'pending' && new Date(t.scheduled_for) <= endOfToday);
        const upcomingTasks = allTasks.filter((t: any) => t.status === 'pending' && new Date(t.scheduled_for) > endOfToday && new Date(t.scheduled_for) <= next7);
        const pendingDrafts = (queueData?.queue || []).filter((d: any) => d.status === 'pending');

        // Group upcoming by day
        const upcomingByDay: Record<string, any[]> = {};
        upcomingTasks.forEach((t: any) => {
          const day = new Date(t.scheduled_for).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
          if (!upcomingByDay[day]) upcomingByDay[day] = [];
          upcomingByDay[day].push(t);
        });

        const priorityColor = (p: string) => p === 'URGENT' || p === 'urgent' ? '#c62828' : p === 'HIGH' || p === 'high' ? '#e65100' : p === 'MEDIUM' || p === 'medium' ? colors.gold : '#888';
        const priorityBg = (p: string) => p === 'URGENT' || p === 'urgent' ? '#ffebee' : p === 'HIGH' || p === 'high' ? '#fff3e0' : p === 'MEDIUM' || p === 'medium' ? '#FBF4DA' : '#f5f5f5';

        const handleApproveTask = async (taskId: string) => {
          try { await axiosInstance.post(`/ai-coach/upcoming/${taskId}/approve`); toast.success('Task approved'); setAutomationsData(null); } catch { toast.error('Failed to approve'); }
        };
        const handleCancelTask = async (taskId: string) => {
          try { await axiosInstance.post(`/ai-coach/upcoming/${taskId}/cancel`); toast.success('Task cancelled'); setAutomationsData(null); } catch { toast.error('Failed to cancel'); }
        };
        const handleSendDraft = async (id: string) => {
          try { await axiosInstance.post(`/ai-coach/queue/${id}/send`); toast.success('Email sent!'); setQueueData(null); } catch { toast.error('Failed to send'); }
        };
        const handleDismissDraft = async (id: string) => {
          try { await axiosInstance.post(`/ai-coach/queue/${id}/complete`); toast.success('Dismissed'); setQueueData(null); } catch { toast.error('Failed to dismiss'); }
        };

        return (
          <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
            {/* Hero Banner */}
            <div style={{ background: `linear-gradient(135deg, ${colors.navy}, #1a2d4a)`, borderRadius: 12, padding: 24, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase' }}>Pipeline Automations</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#fff', fontFamily: fonts.display }}>Your AI Workflow Dashboard</div>
                <div style={{ fontSize: 13, color: '#8FA3BF', marginTop: 4 }}>Review, approve, and manage AI-generated actions for your pipeline</div>
              </div>
              <div style={{ display: 'flex', gap: 16, textAlign: 'center' }}>
                <div><div style={{ fontSize: 28, fontWeight: 700, color: todayQueue.length > 0 ? '#ef5350' : colors.gold, fontFamily: fonts.display }}>{todayQueue.length}</div><div style={{ fontSize: 10, color: '#8FA3BF' }}>Due Today</div></div>
                <div><div style={{ fontSize: 28, fontWeight: 700, color: '#2980b9', fontFamily: fonts.display }}>{upcomingTasks.length}</div><div style={{ fontSize: 10, color: '#8FA3BF' }}>This Week</div></div>
                <div><div style={{ fontSize: 28, fontWeight: 700, color: '#27ae60', fontFamily: fonts.display }}>{pendingDrafts.length}</div><div style={{ fontSize: 10, color: '#8FA3BF' }}>Drafts Ready</div></div>
              </div>
            </div>

            {/* Stats Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Urgent', value: todayQueue.filter((t: any) => (t.priority || '').toLowerCase() === 'urgent').length, color: '#c62828' },
                { label: 'High Priority', value: todayQueue.filter((t: any) => (t.priority || '').toLowerCase() === 'high').length, color: '#e65100' },
                { label: 'Emails to Send', value: pendingDrafts.filter((d: any) => d.action_type === 'draft_email').length, color: '#2980b9' },
                { label: 'Calls to Schedule', value: pendingDrafts.filter((d: any) => d.action_type === 'schedule_call').length, color: '#27ae60' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 10, padding: 16, borderTop: `3px solid ${s.color}` }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: colors.navy }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* TODAY'S QUEUE */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faCalendarDay} style={{ color: colors.gold }} /> Today's Queue ({todayQueue.length})
                </h4>
                {todayQueue.length === 0 && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>No items due today. You're all caught up!</div>}
                {todayQueue.slice(0, 10).map((t: any, i: number) => (
                  <div key={t.task_id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < Math.min(todayQueue.length, 10) - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: priorityBg(t.priority), color: priorityColor(t.priority) }}>{(t.priority || 'MEDIUM').toUpperCase()}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{t.lead_first_name || ''} {t.lead_last_name || ''}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{(t.action || t.agent_type || 'Workflow task').replace(/_/g, ' ')}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleApproveTask(t.task_id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, #E8B44A)`, color: colors.navy, fontWeight: 700, cursor: 'pointer' }}>Approve</button>
                      <button onClick={() => handleCancelTask(t.task_id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: `1px solid #ddd`, background: '#fff', color: '#888', cursor: 'pointer' }}>Skip</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* APPROVE & SEND */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faEnvelope} style={{ color: colors.gold }} /> Approve & Send ({pendingDrafts.length})
                </h4>
                {pendingDrafts.length === 0 && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>No drafts waiting for approval.</div>}
                {pendingDrafts.slice(0, 10).map((d: any, i: number) => (
                  <div key={d.id || i} style={{ padding: '10px 0', borderBottom: i < Math.min(pendingDrafts.length, 10) - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: d.action_type === 'draft_email' ? '#e3f2fd' : d.action_type === 'schedule_call' ? '#e8f5e9' : '#f3e5f5', color: d.action_type === 'draft_email' ? '#1565c0' : d.action_type === 'schedule_call' ? '#2e7d32' : '#6a1b9a' }}>{(d.action_type || '').replace(/_/g, ' ').toUpperCase()}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{d.subject || 'No subject'}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4, maxHeight: 36, overflow: 'hidden' }}>{(d.body || '').substring(0, 120)}{(d.body || '').length > 120 ? '...' : ''}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }}>
                        <button onClick={() => handleSendDraft(d.id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, #E8B44A)`, color: colors.navy, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 9 }} /> Send</button>
                        <button onClick={() => handleDismissDraft(d.id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: `1px solid #ddd`, background: '#fff', color: '#888', cursor: 'pointer' }}>Dismiss</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* UPCOMING WORKFLOW - Full width */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}`, gridColumn: '1 / -1' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: colors.gold }} /> Upcoming This Week ({upcomingTasks.length})
                </h4>
                {upcomingTasks.length === 0 && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>No tasks scheduled for the next 7 days.</div>}
                {Object.entries(upcomingByDay).map(([day, tasks]: [string, any[]]) => (
                  <div key={day} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 4, borderBottom: `2px solid ${colors.gold}22` }}>{day}</div>
                    {tasks.map((t: any, i: number) => (
                      <div key={t.task_id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < tasks.length - 1 ? '1px solid #f8f8f8' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: priorityBg(t.priority), color: priorityColor(t.priority) }}>{(t.priority || 'MEDIUM').toUpperCase()}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{t.lead_first_name || ''} {t.lead_last_name || ''}</span>
                          <span style={{ fontSize: 11, color: '#888' }}>— {(t.action || t.agent_type || 'task').replace(/_/g, ' ')}</span>
                        </div>
                        <button onClick={() => handleCancelTask(t.task_id)} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, border: `1px solid #ddd`, background: '#fff', color: '#888', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'compliance' && (
        <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: 0 }}>
              RESPA Compliance Report
            </h2>
            <OutlineButton onClick={handleExportCSV}>
              <FontAwesomeIcon icon={faDownload} style={{ marginRight: 6 }} /> Export CSV
            </OutlineButton>
          </div>

          {/* Compliance banner */}
          {outstandingRespa.length > 0 && (
            <div style={{
              background: 'rgba(231,76,60,0.1)', border: `1px solid ${colors.red}`,
              borderRadius: 8, padding: 16, marginBottom: 24, fontSize: 13, color: colors.red,
            }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8 }} />
              <strong>{outstandingRespa.length} agent(s)</strong> have not yet acknowledged the RESPA disclosure.
              These agents cannot access the platform until they complete the acknowledgment.
            </div>
          )}

          {stats.complianceRate === 100 && agents.length > 0 && (
            <div style={{
              background: 'rgba(39,174,96,0.1)', border: `1px solid ${colors.green}`,
              borderRadius: 8, padding: 16, marginBottom: 24, fontSize: 13, color: colors.green,
            }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 8 }} />
              All agents have acknowledged the RESPA disclosure. Full compliance achieved.
            </div>
          )}

          {/* Agent compliance table */}
          <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 100px 120px 120px',
              padding: '12px 16px', background: colors.navy2, color: colors.muted,
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            }}>
              <span>Agent Name</span>
              <span>Email</span>
              <span>Status</span>
              <span>RESPA</span>
              <span>Date</span>
            </div>
            {agents.map(agent => (
              <div key={agent.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 100px 120px 120px',
                padding: '12px 16px', borderBottom: `1px solid ${colors.border}`,
                fontSize: 13,
              }}>
                <span style={{ fontWeight: 600, color: colors.navy }}>{agent.name}</span>
                <span style={{ color: colors.muted }}>{agent.email}</span>
                <span>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10,
                    background: agent.status === 'active' ? 'rgba(39,174,96,0.1)' : 'rgba(243,156,18,0.1)',
                    color: agent.status === 'active' ? colors.green : colors.orange,
                    fontWeight: 600,
                  }}>
                    {agent.status}
                  </span>
                </span>
                <span>
                  <FontAwesomeIcon
                    icon={agent.respaAcknowledged ? faCheckCircle : faTimesCircle}
                    style={{ color: agent.respaAcknowledged ? colors.green : colors.red, marginRight: 4 }}
                  />
                  <span style={{ fontSize: 11, color: agent.respaAcknowledged ? colors.green : colors.red }}>
                    {agent.respaAcknowledged ? 'Yes' : 'No'}
                  </span>
                </span>
                <span style={{ fontSize: 11, color: colors.muted }}>
                  {agent.respaAcknowledgedAt ? new Date(agent.respaAcknowledgedAt).toLocaleDateString() : '—'}
                </span>
              </div>
            ))}
            {agents.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: colors.muted, fontSize: 13 }}>
                No sponsored agents yet.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── AI Coaching Console ──────────────────────────────────── */}
      {activeTab === 'coaching' && (() => {
        const loadCoachingProfile = async (agentUserId: string) => {
          setCoachAgent(agentUserId);
          window.history.replaceState(null, '', `${location.pathname}?agentId=${agentUserId}`);
          setCoachLoading(true);
          setCoachData(null);
          setCoachPrompts([]);
          setRetentionDeals(null);
          setPastClosings(null);
          setSellScores([]);
          setCoachChat([]);
          try {
            const { data } = await axiosInstance.get(`/sponsor/coaching/${agentUserId}`);
            if (data.success) {
              setCoachData(data);
              // Auto-load retention deals if MLS ID found
              if (data.agent?.mlsId) {
                axiosInstance.get(`/broker/retention/agent/${data.agent.mlsId}/lost-deals`).then(({ data: retData }) => {
                  if (retData.success) setRetentionDeals(retData);
                }).catch(() => {});
              }
              axiosInstance.get(`/sponsor/coaching/${agentUserId}/past-closings`).then(({ data: closData }) => {
                console.log('[COACHING] Past closings response:', closData?.success, closData?.total_deals);
                if (closData.success) setPastClosings(closData);
              }).catch((err) => { console.error('[COACHING] Past closings failed:', err?.response?.status, err?.message); });
            }
            // Auto-load saved prompts
            const { data: promptData } = await axiosInstance.get(`/sponsor/coaching/${agentUserId}/prompts`);
            if (promptData.success && promptData.prompts?.length > 0) setCoachPrompts(promptData.prompts);
          } catch { }
          setCoachLoading(false);
        };

        const generatePrompts = async (refresh = false) => {
          if (!coachAgent) return;
          setPromptsLoading(true);
          try {
            const { data } = await axiosInstance.get(`/sponsor/coaching/${coachAgent}/prompts${refresh ? '?refresh=true' : ''}`);
            if (data.success) setCoachPrompts(data.prompts || []);
          } catch { }
          setPromptsLoading(false);
        };

        const selectedAgent = agents.find((a: any) => (a.userId || a.id) === coachAgent);
        const fmt = (v: any) => v ? `$${Number(v).toLocaleString()}` : '—';

        return (
        <SplitPanel
          selectedItem={!!coachAgent}
          onBack={() => { setCoachAgent(''); setCoachData(null); }}
          left={
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Select Agent to Coach ({filteredAgents.filter((a: any) => ['active', 'discovered', 'invited', 'pending'].includes(a.status)).length})</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(['docx', 'xlsx'] as const).map(fmt => (
                      <button
                        key={fmt}
                        disabled={!!reportDownloading}
                        onClick={async () => {
                          setReportDownloading(fmt);
                          try {
                            const res = await axiosInstance.get(`/sponsor/coaching/report?format=${fmt}`, { responseType: 'blob' });
                            // Check if we got JSON error instead of a file
                            if (res.data.type === 'application/json') {
                              const text = await res.data.text();
                              const json = JSON.parse(text);
                              toast.error(json.message || 'No data available');
                            } else {
                              const url = URL.createObjectURL(res.data);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Agent_Intelligence_Report.${fmt}`;
                              a.click();
                              URL.revokeObjectURL(url);
                              toast.success(`${fmt.toUpperCase()} report downloaded`);
                            }
                          } catch { toast.error('Failed to generate report'); }
                          setReportDownloading(null);
                        }}
                        style={{
                          background: 'transparent', border: `1px solid ${colors.gold}`, borderRadius: 4,
                          padding: '3px 8px', color: colors.gold, fontSize: 10, fontWeight: 600,
                          cursor: reportDownloading ? 'not-allowed' : 'pointer', opacity: reportDownloading ? 0.5 : 1,
                        }}
                      >
                        {reportDownloading === fmt ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />}
                        {' '}{fmt === 'docx' ? 'Word' : 'Excel'}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: 8 }}><SearchBar value={search} onChange={setSearch} placeholder="Search agents..." /></div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
              {filteredAgents.filter((a: any) => ['active', 'discovered', 'invited', 'pending'].includes(a.status)).map((agent: any) => (
                <ListItem
                  key={agent.id}
                  title={<span>{agent.name} {agent.status === 'discovered' && <span style={{ fontSize: 9, background: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: 8, marginLeft: 4 }}>MLS</span>}</span>}
                  subtitle={(agent as any).brokerage || agent.email}
                  selected={coachAgent === (agent.userId || agent.id)}
                  onClick={() => {
                    // For discovered agents, use MLS ID to load coaching data
                    const agentMlsId = (agent as any).fundingModel === undefined && agent.email ? agent.email : null;
                    loadCoachingProfile(agent.userId || agent.id);
                  }}
                />
              ))}
              {filteredAgents.filter((a: any) => ['active', 'discovered', 'invited', 'pending'].includes(a.status)).length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: colors.muted, fontSize: 13 }}>No agents to coach yet.</div>
              )}
              </div>
            </div>
          }
          right={
            !coachAgent ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: colors.muted }}>
                <div style={{ textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 48, marginBottom: 16, opacity: 0.3, color: colors.gold }} />
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Select an agent to view coaching insights</div>
                  <div style={{ fontSize: 13, marginTop: 8 }}>AI analyzes production data, retention metrics, and property intelligence</div>
                </div>
              </div>
            ) : coachLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24, color: colors.gold }} />
              </div>
            ) : (
              <AgentCoachingDetail
                agentId={coachAgent}
                agentName={selectedAgent?.name || 'Agent'}
                agentEmail={selectedAgent?.email || ''}
                coachData={coachData}
                retentionDeals={retentionDeals}
                pastClosings={pastClosings}
                setPastClosings={setPastClosings}
                coachPrompts={coachPrompts}
                setCoachPrompts={setCoachPrompts}
                coachChat={coachChat}
                setCoachChat={setCoachChat}
              />
            )
          }
        />
        );
      })()}

      {/* ── Billing ────────────────────────────────────────────────── */}
      {activeTab === 'billing' && (
        <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: '0 0 24px' }}>
            Enterprise Billing
          </h2>

          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24,
            border: `1px solid ${colors.border}`,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              Seat License Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: colors.muted }}>Total seats purchased</span>
                <span style={{ fontWeight: 600, color: colors.navy }}>{totalSeats}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: colors.muted }}>Seats in use</span>
                <span style={{ fontWeight: 600, color: colors.navy }}>{stats.total}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: colors.muted }}>Seats available</span>
                <span style={{ fontWeight: 600, color: colors.green }}>{stats.seatsAvailable}</span>
              </div>
            </div>
          </div>

          {/* Manage Subscription */}
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24,
            border: `1px solid ${colors.border}`,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              Monthly Subscription
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 28, fontWeight: 700, color: colors.navy }}>${(totalSeats * 185).toLocaleString()}<span style={{ fontSize: 14, fontWeight: 400, color: colors.muted }}>/month</span></div>
                <div style={{ fontSize: 13, color: colors.muted }}>{totalSeats} seats × $185/seat</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#e8f5e9', color: '#2e7d32' }}>Active</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={async () => {
                  try {
                    const { data } = await axiosInstance.post('/portal-stripe/create-checkout', {
                      plan: 'sponsor-seats',
                      email: sponsorEmail || '',
                      seatCount: totalSeats + 10,
                      company: ''
                    });
                    if (data.url) window.location.href = data.url;
                  } catch { }
                }}
                style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy }}
              >
                Add 10 Seats (+$1,850/mo)
              </button>
              <button
                onClick={() => window.open('mailto:support@kairosiq.ai?subject=Adjust Seat Count', '_blank')}
                style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: `1px solid ${colors.border}`, cursor: 'pointer',
                  background: '#fff', color: colors.navy }}
              >
                Contact Support
              </button>
            </div>
          </div>

          <div style={{
            background: 'rgba(201,149,42,0.1)', border: `1px solid ${colors.gold}`,
            borderRadius: 8, padding: 16, fontSize: 13, color: colors.gold, lineHeight: 1.6,
            marginBottom: 24,
          }}>
            <strong>Enterprise Licensing:</strong> Your organization has purchased {totalSeats} AISearchPortal
            seats under an enterprise software license agreement. All seats include full AI Pro features.
            Agent access is software-only per the KairosIQ Lender Seat Licensing Policy ($185/seat/month).
            Unused seats are reserved — billing remains fixed regardless of agent activation status.
          </div>

          {/* Cancellation */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #ffcdd2' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#c62828', margin: '0 0 12px', fontFamily: fonts.body }}>
              Cancel Subscription
            </h3>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
              Cancellation requires <strong>30 days written notice</strong>. You and your agents retain full access during the notice period. After cancellation, all sponsored agent accounts are deactivated.
            </p>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to request cancellation? This requires 30 days notice. Your account and all agent access will remain active during the notice period.')) {
                  axiosInstance.post('/sponsor/request-cancel', { brokerageId }).then(({ data }) => {
                    if (data.success) toast.success('Cancellation requested. You will receive a confirmation email. Your account remains active for 30 days.');
                    else toast.error(data.error || 'Failed to submit cancellation');
                  }).catch(() => toast.error('Failed to submit cancellation request'));
                }
              }}
              style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #c62828', cursor: 'pointer',
                background: '#fff', color: '#c62828' }}
            >
              Request Cancellation (30-Day Notice)
            </button>
          </div>
        </div>
      )}

      {/* ── KOPS-642: Team Management ───────────────────────────── */}
      {activeTab === 'team' && (
        <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: 0 }}>
              Team Management
            </h2>
            <button
              onClick={() => setShowTeamInvite(!showTeamInvite)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                color: colors.navy, border: 'none', borderRadius: 8,
                padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faUserPlus} /> Invite Admin
            </button>
          </div>

          <p style={{ fontSize: 14, color: colors.muted, marginBottom: 20, lineHeight: 1.6 }}>
            Team admins have full access to the Lender Portal — pipeline data, coaching intelligence, compliance, and billing.
            All admins see the same data.
          </p>

          {/* Invite form */}
          {showTeamInvite && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: `1px solid ${colors.gold}` }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px' }}>Invite a New Admin</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 12, color: colors.muted, display: 'block', marginBottom: 4 }}>Full Name *</label>
                  <input
                    value={teamInviteData.name}
                    onChange={e => setTeamInviteData(d => ({ ...d, name: e.target.value }))}
                    placeholder="Jane Smith"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: colors.muted, display: 'block', marginBottom: 4 }}>Email *</label>
                  <input
                    value={teamInviteData.email}
                    onChange={e => setTeamInviteData(d => ({ ...d, email: e.target.value }))}
                    placeholder="jane@company.com"
                    type="email"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: colors.muted, display: 'block', marginBottom: 4 }}>Phone</label>
                  <input
                    value={teamInviteData.phone}
                    onChange={e => setTeamInviteData(d => ({ ...d, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 14 }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleTeamInvite}
                  disabled={teamInviting}
                  style={{
                    background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                    color: colors.navy, border: 'none', borderRadius: 6,
                    padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {teamInviting ? 'Sending...' : 'Send Invitation'}
                </button>
                <button
                  onClick={() => setShowTeamInvite(false)}
                  style={{
                    background: 'transparent', color: colors.muted, border: `1px solid ${colors.border}`,
                    borderRadius: 6, padding: '10px 24px', fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Team members list */}
          {teamLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: colors.muted }}>Loading team...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {teamMembers.map(member => (
                <div
                  key={member.id}
                  style={{
                    background: '#fff', borderRadius: 12, padding: 20,
                    border: `1px solid ${member.isCurrentUser ? colors.gold : colors.border}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: member.isPrimaryOwner ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})` : colors.navy,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: member.isPrimaryOwner ? colors.navy : '#fff',
                      fontSize: 16, fontWeight: 700,
                    }}>
                      {(member.firstName?.[0] || member.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: colors.navy }}>
                        {member.name || member.email}
                        {member.isCurrentUser && <span style={{ fontSize: 11, color: colors.gold, marginLeft: 8 }}>(You)</span>}
                        {member.isPrimaryOwner && <span style={{ fontSize: 11, color: colors.gold, marginLeft: 8 }}>Primary</span>}
                      </div>
                      <div style={{ fontSize: 13, color: colors.muted }}>{member.email}</div>
                      {member.phone && <div style={{ fontSize: 12, color: colors.muted }}>{member.phone}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
                      padding: '4px 10px', borderRadius: 4,
                      background: member.status === 'active' ? '#e8f5e9' : '#fff3e0',
                      color: member.status === 'active' ? '#2e7d32' : '#e65100',
                    }}>
                      {member.status}
                    </span>
                    {!member.isCurrentUser && !member.isPrimaryOwner && (
                      <button
                        onClick={() => handleRemoveTeamMember(member.id, member.name || member.email)}
                        style={{
                          background: 'transparent', border: `1px solid #e0e0e0`, borderRadius: 6,
                          padding: '6px 12px', fontSize: 12, color: '#c62828', cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {teamMembers.length === 0 && !teamLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: colors.muted, fontSize: 14 }}>
                  No team members yet. Click "Invite Admin" to add your first team member.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Help & Support ──────────────────────────────────────── */}
      {activeTab === 'settings' && <LenderSettingsTab sponsorType={sponsorType} />}

      {activeTab === 'help' && (
        <div style={{ padding: 24, background: colors.offBg, height: '100%', overflowY: 'auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: '0 0 24px' }}>
            Help & Support
          </h2>

          {/* RESPA Compliance Policy */}
          {(sponsorType === 'lender' || sponsorType === 'title') && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: `1px solid ${colors.border}` }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
                <FontAwesomeIcon icon={faShieldAlt} className="me-2" style={{ color: '#e67e22' }} />RESPA Compliance Policy
              </h3>
              <p style={{ fontSize: 13, color: '#555', lineHeight: 1.8, marginBottom: 16 }}>
                KairosIQ's Sponsor Partnership Program is designed to be fully compliant with the Real Estate Settlement Procedures Act (RESPA), Section 8.
                All sponsored agent accounts operate under the following four pillars:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                {[
                  { title: '1. Software Access Only', desc: 'KairosIQ provides software access only — not marketing services, lead generation, settlement services, or any other thing of value on behalf of the sponsoring entity.' },
                  { title: '2. No Payments or Pass-Throughs', desc: 'Agents receive no payments, fees, rebates, splits, or commissions from the sponsoring entity through this platform.' },
                  { title: '3. No Referral Obligations', desc: 'Agents are under NO obligation to refer mortgage business or any settlement service business back to the sponsoring entity.' },
                  { title: '4. Independent Business Decisions', desc: 'Nothing in the platform implies, suggests, or encourages reciprocal referrals to the sponsoring entity.' },
                ].map((p, i) => (
                  <div key={i} style={{ background: '#fffaf0', borderRadius: 8, padding: 16, border: '1px solid #f0e6d0' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: colors.navy, marginBottom: 6 }}>{p.title}</div>
                    <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6 }}>{p.desc}</div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
                All agents must acknowledge these terms before accessing the platform. Acknowledgment records are available on the Compliance tab.
              </p>
            </div>
          )}

          {/* Data Privacy */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: `1px solid ${colors.border}` }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" style={{ color: '#3498db' }} />Agent Data Privacy
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#e8f5e9', borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32', marginBottom: 8 }}>What You CAN See</div>
                <ul style={{ fontSize: 12, color: '#333', lineHeight: 1.8, margin: 0, paddingLeft: 16 }}>
                  <li>MLS production history (public records)</li>
                  <li>Aggregate retention statistics</li>
                  <li>Property trigger counts (foreclosure, death deed)</li>
                  <li>Year-over-year deal volume</li>
                  <li>AI coaching recommendations (no client names)</li>
                </ul>
              </div>
              <div style={{ background: '#ffebee', borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#c62828', marginBottom: 8 }}>What You CANNOT See</div>
                <ul style={{ fontSize: 12, color: '#333', lineHeight: 1.8, margin: 0, paddingLeft: 16 }}>
                  <li>Agent emails or communications</li>
                  <li>CRM contacts or lead details</li>
                  <li>Client names or personal information</li>
                  <li>Pipeline or deal negotiations</li>
                  <li>Financial or commission data</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: `1px solid ${colors.border}` }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              How Sponsor Coaching Works
            </h3>
            <ol style={{ fontSize: 13, color: '#555', lineHeight: 2.2, paddingLeft: 20 }}>
              <li><strong>Invite agents</strong> from the "Sponsored Agents" tab — they receive a 14-day free trial</li>
              <li><strong>Agents sign up</strong> and acknowledge RESPA terms (lender accounts only)</li>
              <li><strong>AI Coaching tab</strong> shows production metrics, retention intelligence, and coaching prompts</li>
              <li><strong>MLS production data</strong> is from public ARMLS records — not agent-provided</li>
              <li><strong>Coaching recommendations</strong> are AI-generated based on aggregate data — no client PII</li>
              <li><strong>Predictive sell scores</strong> identify which properties are most likely to transact next</li>
            </ol>
          </div>

          {/* AI Disclosure */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: `1px solid ${colors.border}` }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 16px', fontFamily: fonts.body }}>
              AI Disclosure
            </h3>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.8, margin: 0 }}>
              AI coaching prompts and strategy chat are generated by Claude (Anthropic) via AWS Bedrock.
              AI recommendations are based on aggregate MLS data and industry statistics.
              AI does not have access to private client information.
              All AI-generated content should be reviewed by the sponsor before use in coaching sessions.
              AI-powered valuations use proprietary machine learning models trained on comparable sales data.
            </p>
          </div>

          {/* Cancellation Policy */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: `1px solid ${colors.border}` }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 12px', fontFamily: fonts.body }}>
              Cancellation Policy
            </h3>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.8, marginBottom: 0 }}>
              Cancellation requires <strong>30 days written notice</strong> to <a href="mailto:support@kairosiq.ai" style={{ color: colors.gold }}>support@kairosiq.ai</a>.
              During the notice period, you and your agents retain full access. After the notice period:
            </p>
            <ul style={{ fontSize: 13, color: '#555', lineHeight: 1.8, paddingLeft: 20, marginTop: 8 }}>
              <li>All sponsored agent accounts are deactivated</li>
              <li>Agents on "Agent Pays" plans retain their own subscriptions independently</li>
              <li>Sponsor dashboard access is removed</li>
              <li>Agent data is retained for 12 months — agents can reactivate individually</li>
            </ul>
          </div>

          {/* Contact */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: `1px solid ${colors.border}` }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 12px', fontFamily: fonts.body }}>
              Contact Support
            </h3>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 12 }}>
              For questions about your sponsor account, billing, compliance, or agent onboarding:
            </p>
            <div style={{ fontSize: 13 }}>
              <div style={{ marginBottom: 6 }}><strong>Email:</strong> <a href="mailto:support@kairosiq.ai" style={{ color: colors.gold }}>support@kairosiq.ai</a></div>
              <div><strong>Office:</strong> 2700 S Gilbert Rd Ste 5, Chandler, AZ 85286</div>
            </div>
          </div>
        </div>
      )}
    </HubShell>
  );
};

// Backwards-compatible export names
export { SponsorDashboard as LenderSponsorHub };
export default SponsorDashboard;
